import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Divider,
  Collapse,
  Chip,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoIcon from '@mui/icons-material/Info';
import CodeIcon from '@mui/icons-material/Code';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { 
  getSegmentDescription, 
  getSegmentDetailedInfo, 
  codeValueMeanings
} from '../utils/eancomSegmentDescriptions';

/**
 * Component to display EDIFACT reference information
 */
const EdifactReference = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSegment, setExpandedSegment] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedCodeType, setSelectedCodeType] = useState('packageTypes');
  const [codeSearchTerm, setCodeSearchTerm] = useState('');
  
  // All common segments from the segmentDescriptions
  const commonSegments = [
    'UNA', 'UNB', 'UNG', 'UNH', 'UNT', 'UNE', 'UNZ',  // Service segments
    'BGM', 'DTM', 'NAD', 'RFF', 'CTA', 'COM',          // Common header segments
    'TDT', 'CPS', 'PAC', 'ALI', 'LOC', 'FTX',          // Transport and packaging segments
    'LIN', 'QTY', 'MOA', 'PRI', 'TAX',                 // Item segments
    'UNS', 'CNT'                                       // Summary segments
  ];

  // Filter segments based on search term
  const filteredSegments = searchTerm
    ? commonSegments.filter(seg => 
        seg.toLowerCase().includes(searchTerm.toLowerCase()) || 
        getSegmentDescription(seg).toLowerCase().includes(searchTerm.toLowerCase())
      )
    : commonSegments;

  // Filter code values based on search term
  const filteredCodeValues = codeSearchTerm
    ? Object.entries(codeValueMeanings[selectedCodeType] || {}).filter(([code, meaning]) => 
        code.toLowerCase().includes(codeSearchTerm.toLowerCase()) || 
        meaning.toLowerCase().includes(codeSearchTerm.toLowerCase())
      )
    : Object.entries(codeValueMeanings[selectedCodeType] || {});

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleCodeSearchChange = (event) => {
    setCodeSearchTerm(event.target.value);
  };

  const handleToggleExpand = (segment) => {
    setExpandedSegment(expandedSegment === segment ? null : segment);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleCodeTypeChange = (event) => {
    setSelectedCodeType(event.target.value);
    setCodeSearchTerm('');
  };

  // Helper function to get related code type for a segment
  const getRelatedCodeType = (segment) => {
    switch (segment) {
      case 'PAC':
        return 'packageTypes';
      case 'TDT':
        return 'transportModes';
      case 'NAD':
      case 'ALI':
        return 'countryCodes';
      case 'UNH':
        return 'messageTypes';
      case 'QTY':
      case 'MEA':
        return 'measurementUnits';
      default:
        return null;
    }
  };

  // Code type display labels
  const codeTypeLabels = {
    messageTypes: 'Message Types',
    documentTypes: 'Document Types',
    packageTypes: 'Package Types',
    transportModes: 'Transport Modes',
    countryCodes: 'Country Codes',
    measurementUnits: 'Measurement Units',
    currencyCodes: 'Currency Codes'
  };

  return (
    <Paper 
      elevation={1}
      sx={{
        width: '100%',
        my: 2,
        overflow: 'hidden'
      }}
    >
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" gutterBottom>
          EDIFACT Reference
        </Typography>
        
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ mb: 2 }}
        >
          <Tab icon={<ListAltIcon />} label="Segments" />
          <Tab icon={<CodeIcon />} label="Code Values" />
        </Tabs>
        
        {activeTab === 0 && (
          <TextField
            fullWidth
            placeholder="Search for a segment (e.g., NAD, DTM)"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        )}
        
        {activeTab === 1 && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Code Type</InputLabel>
              <Select
                value={selectedCodeType}
                onChange={handleCodeTypeChange}
                label="Code Type"
              >
                {Object.entries(codeTypeLabels).map(([value, label]) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              placeholder="Search for a code or its meaning"
              variant="outlined"
              size="small"
              value={codeSearchTerm}
              onChange={handleCodeSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}
      </Box>
      
      {activeTab === 0 && (
        <List sx={{ maxHeight: '500px', overflow: 'auto' }}>
          {filteredSegments.length > 0 ? (
            filteredSegments.map((segment) => {
              const isExpanded = expandedSegment === segment;
              const segmentInfo = getSegmentDetailedInfo(segment);
              
              return (
                <React.Fragment key={segment}>
                  <ListItem
                    button
                    onClick={() => handleToggleExpand(segment)}
                    sx={{
                      transition: 'background-color 0.2s',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Chip 
                            label={segment} 
                            color="primary" 
                            size="small" 
                            sx={{ mr: 1.5, fontWeight: 'bold' }} 
                          />
                          <Typography variant="body2">
                            {getSegmentDescription(segment)}
                          </Typography>
                        </Box>
                      }
                    />
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </ListItem>
                  
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Box sx={{ p: 2, backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
                      {segmentInfo ? (
                        <>
                          <Typography variant="subtitle1" gutterBottom color="primary">
                            {segmentInfo.title}
                          </Typography>
                          
                          <Typography variant="body2" paragraph>
                            <strong>Function:</strong> {segmentInfo.function}
                          </Typography>
                          
                          {segmentInfo.notes && (
                            <Typography variant="body2" paragraph sx={{ fontStyle: 'italic' }}>
                              <strong>Note:</strong> {segmentInfo.notes}
                            </Typography>
                          )}
                          
                          {segmentInfo.elements && (
                            <>
                              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                                Element Structure:
                              </Typography>
                              
                              <Box sx={{ ml: 1 }}>
                                {segmentInfo.elements.map((element, idx) => (
                                  <Box key={idx} sx={{ mb: 1 }}>
                                    <Typography variant="body2" component="div" sx={{ fontWeight: 'bold' }}>
                                      {element.id}: {element.name}
                                      {element.mandatory && (
                                        <Chip
                                          label="Mandatory"
                                          size="small"
                                          color="secondary"
                                          sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                                        />
                                      )}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                      {element.description}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            </>
                          )}
                          
                          {segmentInfo.qualifiers && (
                            <>
                              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                                Common Qualifier Codes:
                              </Typography>
                              
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, ml: 1 }}>
                                {Object.entries(segmentInfo.qualifiers).map(([code, meaning]) => (
                                  <Chip
                                    key={code}
                                    label={`${code}: ${meaning}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.75rem' }}
                                  />
                                ))}
                              </Box>
                            </>
                          )}
                          
                          {/* Show related code values if they exist */}
                          {getRelatedCodeType(segment) && (
                            <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(0, 0, 0, 0.04)', borderRadius: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                Related Code Values:
                              </Typography>
                              <Button 
                                size="small" 
                                color="primary" 
                                onClick={() => {
                                  setSelectedCodeType(getRelatedCodeType(segment));
                                  setActiveTab(1);
                                }}
                              >
                                View {codeTypeLabels[getRelatedCodeType(segment)]}
                              </Button>
                            </Box>
                          )}
                        </>
                      ) : (
                        <Alert
                          severity="info"
                          icon={<InfoIcon fontSize="inherit" />}
                          sx={{ mb: 0 }}
                        >
                          Detailed information not available for this segment.
                        </Alert>
                      )}
                    </Box>
                  </Collapse>
                  
                  <Divider component="li" />
                </React.Fragment>
              );
            })
          ) : (
            <ListItem>
              <ListItemText 
                primary="No segments found" 
                secondary="Try a different search term" 
              />
            </ListItem>
          )}
        </List>
      )}
      
      {activeTab === 1 && (
        <TableContainer sx={{ maxHeight: '500px' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell width="30%"><strong>Code</strong></TableCell>
                <TableCell><strong>Meaning</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCodeValues.length > 0 ? (
                filteredCodeValues.map(([code, meaning]) => (
                  <TableRow key={code} hover>
                    <TableCell>
                      <Chip 
                        label={code} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{meaning}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    No code values found. Try a different search term.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default EdifactReference; 